import {
  ANROK_DEFAULT_TAX_METHOD,
  AnrokIntegrationSettings,
  AnrokIntegrationSettingsSchema,
  INTEGRATION_TYPES,
  INTEGRATION_TYPES_V3,
} from '@vy/integrations-types';
import { raise, raiseUnsupported, validateNotNullOrUndefined } from '@vy/utils';
import { useMemo } from 'react';
import { useIntegrationSettings } from '../../../../../../../hooks/requests/use-integration-settings';
import { AnrokConfigurationFormValues } from '../AnrokConfigurationFormSchema';

type FetchAnrokSettingsResult = {
  integrationId: string;
  configuration: AnrokConfigurationFormValues;
};

export function useFetchAnrokSettings(): FetchAnrokSettingsResult {
  const integrationSettingsResponse = useIntegrationSettings(INTEGRATION_TYPES_V3.ANROK);
  const anrokIntegrationSettings = integrationSettingsResponse.data;

  validateNotNullOrUndefined(anrokIntegrationSettings, 'anrokIntegrationSettings');

  const parsedAnrokIntegrationSettings = AnrokIntegrationSettingsSchema.parse(
    anrokIntegrationSettings.integrationSettings,
  );

  const mappedSettings = mapIntegrationSettingsToFormValues(parsedAnrokIntegrationSettings);

  return useMemo(
    () => ({
      integrationId: anrokIntegrationSettings.integrationId,
      configuration: mappedSettings,
    }),
    [anrokIntegrationSettings.integrationId, mappedSettings],
  );
}

function mapIntegrationSettingsToFormValues(
  integrationSettings: AnrokIntegrationSettings,
): AnrokConfigurationFormValues {
  if (integrationSettings.integrationProvider !== INTEGRATION_TYPES.ANROK) {
    raiseUnsupported(integrationSettings.integrationProvider);
  }

  const config = integrationSettings.integrationConfiguration;

  if (!config) {
    raise('Integration configuration is missing');
  }

  const authConfig = config.authConfig;

  validateNotNullOrUndefined(authConfig.apiKey, 'apiKey');

  return {
    apiKey: authConfig.apiKey,
    defaultExternalProductId: config.generalConfig.defaultExternalProductId ?? '',
    defaultTaxMethod: config.generalConfig.defaultTaxMethod ?? ANROK_DEFAULT_TAX_METHOD.AUTO,
  };
}
